import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, AlertTriangle, Minus, ArrowRight, ShieldCheck, Sparkles, ExternalLink, X } from "lucide-react";
import { ScenarioMap } from "./ScenarioMap";
import { SynthesisNarrative } from "./SynthesisNarrative";
import { ShareButton } from "./ShareButton";
import { computeCoverage } from "../lib/coverage";
import { supportingFindings, frameworkChips } from "../lib/obligations";
import { scenarioTags, pickCase, mapSector } from "../lib/enforcement";
import { extSource, isAssessment } from "../lib/url";
import { RiskExample } from "./RiskExample";

const STATUS = {
  required: { label: "Action needed", icon: AlertTriangle, bg: "#FC5185", fg: "#FFFFFF", accent: "#FC5185" },
  recommended: { label: "Recommended", icon: Sparkles, bg: "#2FE1EC", fg: "#364F6B", accent: "#2FE1EC" },
  covered: { label: "Already covered", icon: Check, bg: "#E6F7F7", fg: "#364F6B", accent: "#3FC1C9" },
  not_needed: { label: "Not needed", icon: Minus, bg: "#EEF2F6", fg: "#667085", accent: "#E4E7EC" },
};

// Quick conversational corrections per finding, keyed to the underlying facts.
const CORRECTIONS = {
  eu_rep: [
    { t: "We already have an EU representative", set: { has_eu_rep: true } },
    { t: "We do not serve EU users", set: { eu_residents: false } },
    { t: "Actually we need this", set: { eu_residents: true, established_outside: true, has_eu_rep: false } },
  ],
  uk_rep: [
    { t: "We already have a UK representative", set: { has_uk_rep: true } },
    { t: "We do not serve UK users", set: { uk_residents: false } },
    { t: "Actually we need this", set: { uk_residents: true, established_outside: true, has_uk_rep: false } },
  ],
  swiss_rep: [
    { t: "We already have a Swiss representative", set: { has_swiss_rep: true } },
    { t: "We do not serve Swiss users", set: { swiss_residents: false } },
    { t: "Actually we need this", set: { swiss_residents: true, established_outside: true, has_swiss_rep: false } },
  ],
  eu_ai_rep: [
    { t: "We do not use AI affecting users", set: { uses_ai: false } },
    { t: "Actually we do use AI", set: { uses_ai: true } },
  ],
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status];
  const Icon = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.fg }} data-testid={`status-${status}`}>
      <Icon size={13} strokeWidth={2.5} /> {s.label}
    </span>
  );
};

// Shown where a source link would be when the fact was inferred by us rather
// than taken from a published page (upstream marks these "inference").
const AssessmentTag = () => (
  <span className="inline-flex items-center gap-1 text-[12px] text-mutedink" title="Inferred by our assessment, not from a published source" data-testid="our-assessment"><Sparkles size={11} /> our assessment</span>
);

const SOURCE_FIELD = { eu_rep: ["has_eu_rep", "eu_residents"], uk_rep: ["has_uk_rep", "uk_residents"], swiss_rep: ["has_swiss_rep", "swiss_residents"], eu_ai_rep: ["uses_ai"] };

export const CoverageAssessment = ({ sessionId, company, domain, searchedUrl, assumptions, fieldConfidence, graphFacts, sourceKind = "graph", correctedKeys = [], fromPrice, registerUrl, enforcementCases = [], revision = 0, onChange, onUndo, onSignup }) => {
  const [openFix, setOpenFix] = useState(null);
  const [activeChip, setActiveChip] = useState(null);
  const coverage = computeCoverage(assumptions);
  const correctedSet = new Set(correctedKeys);
  const idCorrected = (id) => (SOURCE_FIELD[id] || []).some((k) => correctedSet.has(k));
  const chipTarget = (name) => {
    const n = String(name).toLowerCase();
    if (n.includes("eu gdpr")) return "coverage-eu_rep";
    if (n.includes("uk gdpr")) return "coverage-uk_rep";
    if (n.includes("fadp") || n.includes("swiss")) return "coverage-swiss_rep";
    if (n.includes("ai act")) return "coverage-eu_ai_rep";
    if (n.includes("pecr") || n.includes("eprivacy")) return "supporting-findings";
    return null;
  };
  const chipKeys = (name) => {
    const n = String(name).toLowerCase();
    if (n.includes("eu gdpr")) return ["eu_residents"];
    if (n.includes("uk gdpr")) return ["uk_residents"];
    if (n.includes("fadp") || n.includes("swiss")) return ["swiss_residents"];
    if (n.includes("ai act")) return ["uses_ai"];
    return [];
  };
  const scrollTo = (tid) => {
    const el = document.querySelector(`[data-testid="${tid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("flash-highlight");
    void el.offsetWidth;
    el.classList.add("flash-highlight");
    setTimeout(() => el.classList.remove("flash-highlight"), 1300);
  };
  const updatedBadge = (keys) => (
    <button onClick={() => onUndo && onUndo(keys)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0 hover:opacity-80 transition-opacity" style={{ background: "#FFE3EC", color: "#FC5185" }} data-testid="corrected-badge" title="Undo this correction">Updated <X size={10} /></button>
  );
  const actionable = coverage.filter((c) => c.status === "required" || c.status === "recommended");
  const handled = coverage.filter((c) => c.status === "covered" || c.status === "not_needed");
  const requiredCount = coverage.filter((c) => c.status === "required").length;
  const findings = supportingFindings(graphFacts);
  const chips = frameworkChips(graphFacts, assumptions);
  const heading = company && company !== "your company" ? `What ${company} needs` : "What your business needs";
  const riskCase = pickCase(enforcementCases, scenarioTags(coverage, findings), mapSector(assumptions?.sector));

  const sourceFor = (id) => {
    for (const f of SOURCE_FIELD[id] || []) {
      const s = fieldConfidence?.[f]?.source;
      if (s) return s;
    }
    return null;
  };

  const applyFix = (set) => {
    Object.entries(set).forEach(([k, v]) => onChange(k, v));
    setOpenFix(null);
  };

  return (
    <div data-testid="coverage-assessment">
      {/* Scenario header */}
      <section className="border-b hairline">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 text-[12.5px] font-medium text-asphalt bg-white px-3 py-1.5 rounded-full border hairline">
              <ShieldCheck size={14} /> Indicative, not legal advice
            </span>
            <div className="mt-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] text-asphalt">{heading}</h1>
                {(domain || searchedUrl) && (
                  <div className="mt-1.5 text-[13px] text-mutedink truncate" data-testid="searched-url">{domain || searchedUrl}</div>
                )}
              </div>
              <ShareButton domain={domain || searchedUrl} className="mt-1 shrink-0" />
            </div>
            <SynthesisNarrative sessionId={sessionId} company={company} assumptions={assumptions} facts={graphFacts} coverage={coverage} revision={revision} />
            {sourceKind !== "graph" && (
              <div className="mt-4 rounded-lg border hairline px-4 py-3 text-[13px] text-asphalt leading-relaxed" style={{ background: "#FFF3F6", borderColor: "#FCD9E2" }} data-testid="lowconf-banner">
                I could not fully retrieve public records for this website, so the findings below are general starting points rather than a tailored assessment. Tell the copilot about your business and I will refine them.
              </div>
            )}
            {sourceKind === "graph" && requiredCount > 0 && (
              <p className="mt-4 inline-flex items-center gap-2 text-[14px] font-bold" style={{ color: "#FC5185" }}>
                <AlertTriangle size={16} /> {requiredCount} representative{requiredCount > 1 ? "s" : ""} needed for your situation
              </p>
            )}
            {chips.length > 0 && (
              <div className="mt-6" data-testid="frameworks-block">
                <div className="text-[11px] font-bold uppercase tracking-wide text-mutedink mb-2.5">Frameworks we checked <span className="normal-case font-medium text-mutedink/60">(tap for why)</span></div>
                <div className="flex flex-wrap gap-2" data-testid="framework-chips">
                  {chips.map((c) => {
                    const isActive = activeChip === c.name;
                    const cls = c.applicable
                      ? "border-aqua bg-aqua-tint text-asphalt"
                      : "border-[#E4E7EC] bg-[#F4F6F9] text-mutedink";
                    const tid = `fw-chip-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
                    return (
                      <button
                        key={c.name}
                        onClick={() => {
                          if (c.applicable) {
                            const t = chipTarget(c.name);
                            if (t) { scrollTo(t); setActiveChip(null); return; }
                          }
                          setActiveChip(isActive ? null : c.name);
                        }}
                        className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-2.5 py-1 rounded-full border transition-colors hover:border-asphalt/40 ${cls} ${isActive ? "ring-2 ring-asphalt/15" : ""}`}
                        data-testid={tid}
                      >
                        {c.applicable ? <Check size={12} strokeWidth={3} color="#3FC1C9" /> : <Minus size={12} color="#98A2B3" />}
                        <span className={c.applicable ? "" : "line-through decoration-[#C7CDD6]"}>{c.name}</span>
                        {c.corrected && <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onUndo && onUndo(chipKeys(c.name)); }} className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 rounded cursor-pointer hover:underline" style={{ background: "#FFE3EC", color: "#FC5185" }} title="Undo this correction">updated <X size={9} /></span>}
                      </button>
                    );
                  })}
                </div>
                {activeChip && (() => {
                  const c = chips.find((x) => x.name === activeChip);
                  if (!c) return null;
                  return (
                    <div className="mt-3 rounded-lg border hairline bg-white p-3.5 text-[13px] text-asphalt/85 leading-relaxed" data-testid="framework-detail">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: c.applicable ? "#3FC1C9" : "#98A2B3" }}>{c.applicable ? "In scope" : "Not in scope"}</span>
                        <span className="font-bold text-asphalt text-[13.5px]">{c.name}</span>
                      </div>
                      <span>{c.rationale || "No further detail available."}</span>
                      {extSource(c.source) ? <a href={extSource(c.source)} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[12px] text-mutedink hover:text-teal align-middle">source <ExternalLink size={11} /></a> : isAssessment(c.source) && <span className="ml-2 align-middle"><AssessmentTag /></span>}
                    </div>
                  );
                })()}
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <ScenarioMap height={360} />
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1000px] mx-auto px-5 lg:px-8 py-10">
        <h2 className="text-2xl font-bold text-asphalt mb-1">Your key findings</h2>
        <p className="text-mutedink text-[14px] mb-6">Tailored to your exact scenario from what we found. If anything is wrong, tell the copilot or hit "Not right?" on any finding.</p>

        <div className="space-y-4" data-testid="coverage-checklist">
          {actionable.map((c) => {
            const rawSrc = sourceFor(c.id);
            const src = extSource(rawSrc);
            return (
              <div key={c.id} className="rounded-xl border-l-4 border hairline bg-white p-5" style={{ borderLeftColor: STATUS[c.status].accent }} data-testid={`coverage-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#3FC1C9" }}>{c.art}</div>
                    <h3 className="text-lg font-bold text-asphalt leading-tight">{c.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {idCorrected(c.id) && updatedBadge(SOURCE_FIELD[c.id] || [])}
                    <StatusBadge status={c.status} />
                  </div>
                </div>
                <p className="mt-3 text-[13.5px] text-asphalt/85 leading-relaxed">{c.reason}</p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {c.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] text-asphalt/80">
                      <Check size={15} color="#2FE1EC" strokeWidth={3} className="mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center justify-between gap-3 border-t hairline pt-3">
                  <div className="text-[12px] text-mutedink">Provided by GDPRLocal group</div>
                  <div className="flex items-center gap-3">
                    {src ? <a href={src} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-asphalt hover:text-teal">source <ExternalLink size={11} /></a> : isAssessment(rawSrc) && <AssessmentTag />}
                    <button onClick={() => setOpenFix(openFix === c.id ? null : c.id)} className="text-[12px] font-medium underline text-mutedink hover:text-asphalt" data-testid={`notright-${c.id}`}>Not right?</button>
                  </div>
                </div>
                {openFix === c.id && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`fix-${c.id}`}>
                    {(CORRECTIONS[c.id] || []).map((f) => (
                      <button key={f.t} onClick={() => applyFix(f.set)} className="text-[12.5px] px-3 py-1.5 rounded-full border hairline bg-white hover:border-pink hover:bg-pink-tint transition-colors" style={{ "--tw-hover": "" }}>
                        {f.t}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {riskCase && <RiskExample case={riskCase} />}

        {handled.length > 0 && (
          <div className="mt-6 rounded-xl border hairline bg-white divide-y divide-[#E4E7EC]" data-testid="coverage-handled">
            {handled.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3.5" data-testid={`coverage-${c.id}`}>
                <div className="min-w-0">
                  <span className="font-medium text-asphalt text-[14px]">{c.name}</span>
                  <span className="text-[12.5px] text-mutedink ml-2">{c.reason}</span>
                  {extSource(sourceFor(c.id)) ? <a href={extSource(sourceFor(c.id))} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[12px] text-asphalt hover:text-teal align-middle">source <ExternalLink size={10} /></a> : isAssessment(sourceFor(c.id)) && <span className="ml-2 align-middle"><AssessmentTag /></span>}
                  <button onClick={() => setOpenFix(openFix === c.id ? null : c.id)} className="ml-2 text-[12px] underline text-mutedink hover:text-asphalt" data-testid={`notright-${c.id}`}>Not right?</button>
                  {openFix === c.id && (
                    <span className="ml-2 inline-flex flex-wrap gap-2" data-testid={`fix-${c.id}`}>
                      {(CORRECTIONS[c.id] || []).map((f) => (
                        <button key={f.t} onClick={() => applyFix(f.set)} className="text-[12px] px-2.5 py-1 rounded-full border hairline bg-white hover:border-pink hover:bg-pink-tint">{f.t}</button>
                      ))}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {idCorrected(c.id) && updatedBadge(SOURCE_FIELD[c.id] || [])}
                  <StatusBadge status={c.status} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Supporting findings: broader obligations mapped to the exact GDPRLocal service */}
        {findings.length > 0 && (
          <div className="mt-10" data-testid="supporting-findings">
            <h2 className="text-2xl font-bold text-asphalt mb-1">Beyond representation</h2>
            <p className="text-mutedink text-[14px] mb-6">Other obligations we spotted for your exact situation, and how GDPRLocal covers them.</p>
            <div className="space-y-3">
              {findings.map((f) => (
                <div key={f.id} className="rounded-xl border hairline bg-white p-5" data-testid={`finding-${f.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      {f.framework && <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#3FC1C9" }}>{f.framework}</div>}
                      <h3 className="text-[15.5px] font-bold text-asphalt leading-tight">{f.title}</h3>
                    </div>
                    {f.priority && (
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: f.priority === "high" ? "#FC5185" : "#EEF2F6", color: f.priority === "high" ? "#FFFFFF" : "#667085" }}>
                        {f.priority === "high" ? "Priority" : "Noted"}
                      </span>
                    )}
                  </div>
                  {f.why && <p className="mt-2.5 text-[13.5px] text-asphalt/85 leading-relaxed">{f.why}</p>}
                  {(f.serviceLabel || f.source) && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t hairline pt-3">
                      {f.serviceLabel ? (
                        <a href={f.serviceUrl || "https://gdprlocal.com/contact/"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-asphalt hover:text-teal" data-testid={`finding-service-${f.id}`}>
                          How GDPRLocal helps: {f.serviceLabel} <ArrowRight size={14} />
                        </a>
                      ) : <span />}
                      {extSource(f.source) ? (
                        <a href={extSource(f.source)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-mutedink hover:text-teal" data-testid={`finding-source-${f.id}`}>source <ExternalLink size={11} /></a>
                      ) : (isAssessment(f.source) && <AssessmentTag />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Register CTA, no prices on page */}
        <div className="mt-8 rounded-2xl p-7 lg:p-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" style={{ background: "#364F6B" }} data-testid="register-cta">
          <div>
            <h3 className="text-white text-2xl font-bold">Ready to get covered?</h3>
            <p className="text-white/75 text-[14px] mt-2 max-w-lg leading-relaxed">Register free, sign your agreement, and activate representative services in the portal, where your exact pricing is always visible.</p>
            <div className="flex flex-wrap gap-4 mt-3 text-white/85 text-[13px]">
              <span className="inline-flex items-center gap-1.5"><Check size={14} color="#2FE1EC" strokeWidth={3} /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} color="#2FE1EC" strokeWidth={3} /> Easy setup</span>
            </div>
          </div>
          <div className="shrink-0">
            <div className="text-white/70 text-[13px] mb-2">Plans start at <span className="text-white font-bold text-lg">{fromPrice}</span></div>
            <button onClick={onSignup} className="btn-aqua px-7 py-3.5 inline-flex items-center gap-2 text-[16px]" data-testid="coverage-signup">Register free <ArrowRight size={18} /></button>
            <a href="https://gdprlocal.com/contact/" className="block text-center mt-3 text-[13px] text-white/70 hover:text-aqua underline" data-testid="coverage-talk-human">Talk to a human</a>
          </div>
        </div>
      </div>
    </div>
  );
};
